import React, { useEffect, useMemo, useState } from 'react'
import {
  ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar,
} from 'recharts'

const API_BASE = 'https://garibcasinos.com/api' // ✅ tu base real

function yyyymmNow() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}`
}

function money(x) {
  const n = Number(x || 0)
  return n.toLocaleString('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  })
}

async function getJSON(url, params) {
  const u = new URL(url)
  Object.entries(params || {}).forEach(([k, v]) => u.searchParams.set(k, v))

  try {
    const res = await fetch(u.toString(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })

    const text = await res.text()

    if (!res.ok) {
      console.error('[API HTTP ERROR]', res.status, u.toString(), text)
      throw new Error(`HTTP ${res.status}`)
    }

    try {
      return JSON.parse(text)
    } catch (e) {
      console.error('[API NO JSON]', u.toString(), text)
      throw new Error('Respuesta no JSON')
    }
  } catch (err) {
    console.error('[FETCH FALLÓ]', u.toString(), err)
    throw err
  }
}

function downloadCSV(filename, rows) {
  if (!rows || !rows.length) return

  const esc = (v) => {
    const s = String(v === null || typeof v === 'undefined' ? '' : v)
    // escapar comillas dobles (sin replaceAll)
    const ss = s.split('"').join('""')
    if (/[",\n]/.test(ss)) return `"${ss}"`
    return ss
  }

  const headers = Object.keys(rows[0] || {})
  const csv = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => esc(r[h])).join(',')),
  ].join('\n')

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export default function DashboardAlmuerzosAdmin() {
  const [mes, setMes] = useState(yyyymmNow())
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const [resumen, setResumen] = useState(null)
  const [serie, setSerie] = useState([])
  const [porPlato, setPorPlato] = useState([])
  const [topPerdida, setTopPerdida] = useState([])
  const [proy, setProy] = useState(null)

  const [openModal, setOpenModal] = useState(false)
  const [loadingFantasma, setLoadingFantasma] = useState(false)
  const [fantasma, setFantasma] = useState([])

  const loadAll = async (mesSel) => {
    setLoading(true)
    setErrorMsg('')

    const endpoints = [
      { key: 'resumen', fn: () => getJSON(`${API_BASE}/dashboardResumen.php`, { mes: mesSel }) },
      { key: 'serie', fn: () => getJSON(`${API_BASE}/dashboardSerieDiaria.php`, { mes: mesSel }) },
      { key: 'porPlato', fn: () => getJSON(`${API_BASE}/dashboardPorPlato.php`, { mes: mesSel }) },
      { key: 'topPerdida', fn: () => getJSON(`${API_BASE}/dashboardTopPerdida.php`, { mes: mesSel }) },
      { key: 'proy', fn: () => getJSON(`${API_BASE}/dashboardProyecciones.php`, { mes: mesSel }) },
    ]

    try {
      const results = await Promise.allSettled(endpoints.map((e) => e.fn()))

      // ayudita: mostrar exactamente qué endpoint falló
      const fallas = []
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          fallas.push(endpoints[i].key)
          console.error('Endpoint falló:', endpoints[i].key, r.reason)
        }
      })
      if (fallas.length) {
        setErrorMsg(`Algunos reportes no cargaron: ${fallas.join(', ')}. Revisa API/CORS o archivos PHP.`)
      }

      const a = results[0].status === 'fulfilled' ? results[0].value : null
      const b = results[1].status === 'fulfilled' ? results[1].value : null
      const c = results[2].status === 'fulfilled' ? results[2].value : null
      const d = results[3].status === 'fulfilled' ? results[3].value : null
      const e = results[4].status === 'fulfilled' ? results[4].value : null

      setResumen(a && a.data ? a.data : null)

      setSerie(
        (b && b.data ? b.data : []).map((x) => ({
          ...x,
          programados: Number(x.programados || 0),
          consumidos: Number(x.consumidos || 0),
          no_consumidos: Number(x.no_consumidos || 0),
          perdida_estimada: Number(x.perdida_estimada || 0),
        }))
      )

      setPorPlato(
        (c && c.data ? c.data : []).map((x) => ({
          ...x,
          plato_nombre: x.plato_nombre || x.almuerzo || '-',
          programados: Number(x.programados || 0),
          consumidos: Number(x.consumidos || 0),
          no_consumidos: Number(x.no_consumidos || 0),
          perdida_estimada: Number(x.perdida_estimada || 0),
          margen_estimado: Number(x.margen_estimado || 0),
        }))
      )

      setTopPerdida(
        (d && d.data ? d.data : []).map((x) => ({
          ...x,
          plato_nombre: x.plato_nombre || x.almuerzo || '-',
          perdida_estimada: Number(x.perdida_estimada || 0),
        }))
      )

      setProy(e && e.data ? e.data : null)
    } finally {
      setLoading(false)
    }
  }

  const loadFantasma = async () => {
    setLoadingFantasma(true)
    setErrorMsg('')
    try {
      const r = await getJSON(`${API_BASE}/dashboardFantasma.php`, { mes })
      setFantasma(
        (r && r.data ? r.data : []).map((x) => ({
          ...x,
          plato_nombre: x.plato_nombre || x.almuerzo || '-',
          rut_norm: x.rut_norm || x.rut || '',
          fecha: x.fecha || '',
          costo_elaboracion: Number(x.costo_elaboracion || 0),
          precio_venta: Number(x.precio_venta || 0),
        }))
      )
    } catch (e) {
      setErrorMsg('No se pudo cargar “No consumidos”. Revisa dashboardFantasma.php y CORS.')
      throw e
    } finally {
      setLoadingFantasma(false)
    }
  }

  useEffect(() => {
    loadAll(mes)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes])

  const kpi = useMemo(() => {
    const d = resumen || {}
    return {
      programados: Number(d.programados || 0),
      consumidos: Number(d.consumidos || 0),
      no_consumidos: Number(d.no_consumidos || 0),
      cumplimiento_pct: Number(d.cumplimiento_pct || 0),

      costo_presupuestado: Number(d.costo_presupuestado || 0),
      costo_real: Number(d.costo_real || 0),
      perdida_estimada: Number(d.perdida_estimada || 0),
      ingreso_estimado: Number(d.ingreso_estimado || 0),
      margen_estimado: Number(d.margen_estimado || 0),
    }
  }, [resumen])

  const proyData = useMemo(() => {
    const d = proy || {}
    return {
      ausentismo_pct: Number(d.ausentismo_pct || 0),
      costo_promedio: Number(d.costo_promedio || 0),
      ahorro_si_8pct: Number(d.ahorro_si_8pct || 0),
      ahorro_si_5pct: Number(d.ahorro_si_5pct || 0),
    }
  }, [proy])

  return (
    <div className="dash-page">
      <div className="dash-header">
        <div>
          <h2 className="dash-title">Dashboard Almuerzos (Admin)</h2>
          <div className="dash-subtitle">Presupuesto vs Real + pérdida + proyecciones</div>
        </div>

        <div className="dash-actions">
          <input
            className="dash-input"
            type="month"
            value={mes}
            onChange={(e) => setMes(e.target.value)}
          />

          <button
            className="dash-btn dash-btn-outline"
            onClick={async () => {
              setOpenModal(true)
              if (!fantasma.length) await loadFantasma()
            }}
          >
            Ver No Consumidos
          </button>

          <button
            className="dash-btn"
            onClick={() => downloadCSV(`serie_diaria_${mes}.csv`, serie)}
            disabled={!serie.length}
          >
            CSV Serie
          </button>

          <button
            className="dash-btn"
            onClick={() => downloadCSV(`ranking_platos_${mes}.csv`, porPlato)}
            disabled={!porPlato.length}
          >
            CSV Ranking
          </button>
        </div>
      </div>

      {errorMsg ? (
        <div className="dash-alert">
          {errorMsg}
        </div>
      ) : null}

      {loading ? (
        <div className="dash-loading">Cargando…</div>
      ) : (
        <>
          <div className="dash-grid">
            <KpiCard label="Programados" value={kpi.programados} />
            <KpiCard label="Consumidos" value={kpi.consumidos} />
            <KpiCard label="No consumidos" value={kpi.no_consumidos} />
            <KpiCard label="Cumplimiento" value={`${kpi.cumplimiento_pct}%`} />
          </div>

          <div className="dash-grid">
            <KpiCard label="Costo presupuestado" value={money(kpi.costo_presupuestado)} />
            <KpiCard label="Costo real" value={money(kpi.costo_real)} />
            <KpiCard label="Pérdida estimada" value={money(kpi.perdida_estimada)} />
            <KpiCard label="Margen estimado" value={money(kpi.margen_estimado)} />
          </div>

          <div className="dash-grid">
            <KpiCard label="Ausentismo" value={`${proyData.ausentismo_pct}%`} />
            <KpiCard label="Costo promedio" value={money(proyData.costo_promedio)} />
            <KpiCard label="Ahorro si ausentismo 8%" value={money(proyData.ahorro_si_8pct)} />
            <KpiCard label="Ahorro si ausentismo 5%" value={money(proyData.ahorro_si_5pct)} />
          </div>

          <div className="dash-2col">
            <Section title="Programados vs Consumidos (diario)">
              <div className="dash-chart">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={serie}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="fecha" tick={{ fontSize: 10 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="programados" />
                    <Line type="monotone" dataKey="consumidos" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Section>

            <Section title="Pérdida estimada (diaria)">
              <div className="dash-chart">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={serie}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="fecha" tick={{ fontSize: 10 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="perdida_estimada" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>
          </div>

          <Section title="Top 10 platos por pérdida (Pareto)">
            <div className="dash-chart">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={topPerdida} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="plato_nombre" width={220} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="perdida_estimada" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>

          <Section title="Ranking por plato (sin “FONDO 1”)">
            <div className="dash-table-wrap">
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Plato</th>
                    <th>Programados</th>
                    <th>Consumidos</th>
                    <th>No consumidos</th>
                    <th>Pérdida</th>
                    <th>Margen</th>
                  </tr>
                </thead>
                <tbody>
                  {porPlato.map((r, idx) => (
                    <tr key={`${r.plato_nombre}-${idx}`}>
                      <td>{r.plato_nombre}</td>
                      <td>{r.programados}</td>
                      <td>{r.consumidos}</td>
                      <td>{r.no_consumidos}</td>
                      <td>{money(r.perdida_estimada)}</td>
                      <td>{money(r.margen_estimado)}</td>
                    </tr>
                  ))}
                  {!porPlato.length ? (
                    <tr><td colSpan={6} className="dash-muted">Sin datos</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      )}

      {openModal ? (
        <div className="dash-modal-backdrop" onClick={() => setOpenModal(false)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <div className="dash-modal-title">No consumidos (programó y no almorzó)</div>
              <button className="dash-btn dash-btn-ghost" onClick={() => setOpenModal(false)}>✕</button>
            </div>

            <div className="dash-modal-body">
              {loadingFantasma ? (
                <div className="dash-loading">Cargando…</div>
              ) : (
                <div className="dash-table-wrap">
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>RUT</th>
                        <th>Plato</th>
                        <th>Costo</th>
                        <th>Venta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fantasma.map((r, idx) => (
                        <tr key={`${r.fecha}-${r.rut_norm}-${idx}`}>
                          <td>{r.fecha}</td>
                          <td>{r.rut_norm}</td>
                          <td>{r.plato_nombre}</td>
                          <td>{money(r.costo_elaboracion)}</td>
                          <td>{money(r.precio_venta)}</td>
                        </tr>
                      ))}
                      {!fantasma.length ? (
                        <tr><td colSpan={5} className="dash-muted">Sin datos</td></tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="dash-modal-footer">
              <button className="dash-btn" onClick={() => setOpenModal(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function KpiCard(props) {
  const label = props.label
  const value = props.value
  return (
    <div className="dash-card">
      <div className="dash-card-label">{label}</div>
      <div className="dash-card-value">{value}</div>
    </div>
  )
}

function Section(props) {
  const title = props.title
  const children = props.children
  return (
    <div className="dash-section">
      <div className="dash-section-title">{title}</div>
      {children}
    </div>
  )
}
